/**
 * My Bookings Page - Olympic Fit Wellness
 * Shows member's upcoming and past session bookings
 */
import React, { useState, useEffect } from 'react';
import { format, isPast, isFuture } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  User, 
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Hourglass,
  ChevronRight
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

// Status badge styling
const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Hourglass, label: 'Pending Approval' },
  confirmed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Confirmed' },
  declined: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Declined' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', icon: XCircle, label: 'Cancelled' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Completed' },
  no_show: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'No Show' }
};

export default function MyBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await api.getMyCalendarBookings();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter bookings
  const upcomingBookings = bookings.filter(b => {
    const session = b.calendar_sessions;
    if (!session?.start_time) return false;
    return isFuture(new Date(session.start_time)) && ['pending', 'confirmed'].includes(b.status);
  });

  const pastBookings = bookings.filter(b => {
    const session = b.calendar_sessions;
    if (!session?.start_time) return false;
    return isPast(new Date(session.start_time)) || ['completed', 'cancelled', 'declined', 'no_show'].includes(b.status);
  });

  // Cancel booking
  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    
    setCancelLoading(true);
    try {
      const result = await api.cancelCalendarBooking(selectedBooking.id, cancelReason);
      if (result.success) {
        toast.success(result.message || 'Booking cancelled');
        if (result.refund_status === 'pending') {
          toast.info('Refund request submitted for review');
        } else if (result.refund_status === 'denied') {
          toast.warning(`Refund not eligible (must cancel ${result.cancellation_policy?.cancellation_hours || 24}h in advance)`);
        }
        setShowCancelModal(false);
        setCancelReason('');
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to cancel booking');
    } finally {
      setCancelLoading(false);
    }
  };

  // Booking card component
  const BookingCard = ({ booking }) => {
    const session = booking.calendar_sessions;
    const staff = session?.profiles;
    const status = STATUS_STYLES[booking.status] || STATUS_STYLES.pending;
    const StatusIcon = status.icon;
    const sessionDate = session?.start_time ? new Date(session.start_time) : null;
    const canCancel = ['pending', 'confirmed'].includes(booking.status) && sessionDate && isFuture(sessionDate);

    return (
      <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-lg">{session?.title || 'Session'}</h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${status.bg} ${status.text}`}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </span>
          </div>
          {canCancel && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSelectedBooking(booking);
                setShowCancelModal(true);
              }}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Cancel
            </Button>
          )}
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {sessionDate ? format(sessionDate, 'EEEE, MMMM d, yyyy') : 'Date TBD'}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {sessionDate ? (
              <>
                {format(sessionDate, 'h:mm a')}
                {session?.end_time && ` - ${format(new Date(session.end_time), 'h:mm a')}`}
              </>
            ) : 'Time TBD'}
          </div>
          {staff && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {staff.full_name || 'Instructor'}
            </div>
          )}
        </div>

        {/* Request message or response */}
        {booking.is_request && booking.request_message && (
          <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
            <p className="text-gray-500 text-xs mb-1">Your message:</p>
            <p className="text-gray-700">{booking.request_message}</p>
          </div>
        )}
        {booking.response_message && (
          <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
            <p className="text-blue-600 text-xs mb-1">Instructor response:</p>
            <p className="text-gray-700">{booking.response_message}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">My Bookings</h1>
        <p className="text-gray-600">View and manage your class and session bookings</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upcoming" className="gap-2">
            Upcoming
            {upcomingBookings.length > 0 && (
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                {upcomingBookings.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="past">Past</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : upcomingBookings.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-700">No upcoming bookings</h3>
              <p className="text-gray-500 mb-4">Browse the schedule to book your next session</p>
              <Button onClick={() => window.location.href = '/portal/schedule'}>
                View Schedule
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {upcomingBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : pastBookings.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-700">No past bookings</h3>
              <p className="text-gray-500">Your completed sessions will appear here</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {pastBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this booking?
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium">{selectedBooking.calendar_sessions?.title}</p>
                <p className="text-sm text-gray-600">
                  {selectedBooking.calendar_sessions?.start_time && 
                    format(new Date(selectedBooking.calendar_sessions.start_time), 'PPp')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Reason for cancellation (optional)</label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Let us know why you're cancelling..."
                  rows={3}
                />
              </div>

              <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Refund eligibility depends on cancellation timing and the service's policy.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Booking
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelBooking}
              disabled={cancelLoading}
            >
              {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
